import axios, { AxiosInstance } from 'axios'
import type { LoginCredentials, RegisterData, User } from '@/stores/auth'

class BaserowService {
  private client: AxiosInstance
  private baseURL: string
  private token: string
  private databaseId: string

  constructor() {
    this.baseURL = import.meta.env.VITE_BASEROW_URL
    this.token = import.meta.env.VITE_BASEROW_TOKEN
    this.databaseId = import.meta.env.VITE_BASEROW_DATABASE_ID

    this.client = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Authorization': `Token ${this.token}`,
        'Content-Type': 'application/json'
      },
      timeout: 10000
    })

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        console.log(`📤 Baserow Request: ${config.method?.toUpperCase()} ${config.url}`)
        return config
      },
      (error) => {
        console.error('📤 Baserow Request Error:', error)
        return Promise.reject(error)
      }
    )

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        console.log(`📥 Baserow Response: ${response.status} ${response.config.url}`)
        return response
      },
      (error) => {
        console.error('📥 Baserow Response Error:', error.response?.status, error.message)
        console.error('📥 Baserow Error Details:', error.response?.data)
        console.error('📥 Baserow Full Response:', error.response)
        
        // Handle common errors
        if (error.response?.status === 401) {
          throw new Error('Non autorisé - Vérifiez vos identifiants')
        }
        if (error.response?.status === 404) {
          throw new Error('Ressource non trouvée')
        }
        if (error.response?.status >= 500) {
          throw new Error('Erreur serveur - Veuillez réessayer plus tard')
        }
        
        // Include detailed error information
        const errorDetails = error.response?.data ? JSON.stringify(error.response.data) : 'Détails non disponibles'
        throw new Error(`Erreur API (${error.response?.status}): ${errorDetails}`)
      }
    )
  }

  // Test connection
  async testConnection(): Promise<boolean> {
    try {
      const response = await this.client.get('/api/user/')
      return response.status === 200
    } catch (error) {
      console.error('❌ Baserow connection test failed:', error)
      return false
    }
  }

  // Get all tables (simplified - we know the table IDs)
  async getTables(): Promise<any[]> {
    try {
      // For Baserow, we'll directly use known table IDs
      // This method is kept for compatibility but we access tables directly by ID
      return [
        { id: '796', name: 'Participants' },
        { id: '797', name: 'Hôtes/Lieux' },
        { id: '798', name: 'Rendez-vous d\'Armorique' },
        { id: '799', name: 'Confirmations' },
        { id: '800', name: 'Ressources' },
        { id: '801', name: 'Confirmations Financières' },
        { id: '802', name: 'Sphères Professionnelles' },
        { id: '803', name: 'Domaines d\'Activité' },
        { id: '804', name: 'Contenu Dynamique' },
        { id: '805', name: 'Charte Graphique' }
      ]
    } catch (error) {
      throw new Error(`Failed to get tables: ${error}`)
    }
  }

  // Generic CRUD operations
  async createRecord(tableId: string, data: any): Promise<any> {
    try {
      const response = await this.client.post(`/api/database/rows/table/${tableId}/`, data)
      return response.data
    } catch (error) {
      throw error
    }
  }

  async getRecords(tableId: string, filters = {}, page = 1, size = 100): Promise<any> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
        ...filters
      })

      const response = await this.client.get(`/api/database/rows/table/${tableId}/?${params}`)
      return response.data
    } catch (error) {
      throw error
    }
  }

  async getRecord(tableId: string, recordId: string): Promise<any> {
    try {
      const response = await this.client.get(`/api/database/rows/table/${tableId}/${recordId}/`)
      return response.data
    } catch (error) {
      throw error
    }
  }

  async updateRecord(tableId: string, recordId: string, data: any): Promise<any> {
    try {
      const response = await this.client.patch(`/api/database/rows/table/${tableId}/${recordId}/`, data)
      return response.data
    } catch (error) {
      throw error
    }
  }

  async deleteRecord(tableId: string, recordId: string): Promise<void> {
    try {
      await this.client.delete(`/api/database/rows/table/${tableId}/${recordId}/`)
    } catch (error) {
      throw error
    }
  }

  async searchRecords(tableId: string, query: string, searchFields: string[] = []): Promise<any> {
    try {
      const params = new URLSearchParams({
        search: query
      })

      if (searchFields.length > 0) {
        params.append('search_fields', searchFields.join(','))
      }

      const response = await this.client.get(`/api/database/rows/table/${tableId}/?${params}`)
      return response.data
    } catch (error) {
      throw error
    }
  }

  // Generate default password based on user data
  private generateDefaultPassword(user: any): string {
    const lastName = user['field_7790'] || 'user'
    const firstName = user['field_7792'] || 'user'
    
    // Default password: first 3 chars of last name + first 3 chars of first name + "123"
    const lastPart = lastName.toLowerCase().substring(0, 3).padEnd(3, 'x')
    const firstPart = firstName.toLowerCase().substring(0, 3).padEnd(3, 'x')
    
    return `${lastPart}${firstPart}123`
  }

  // Auth specific methods using existing Baserow table structure (table 796)
  async login(credentials: LoginCredentials): Promise<{ token: string; user: User }> {
    try {
      // Search for user by email in table 796 (field_7789 is E-mail)
      const userResult = await this.searchRecords('796', credentials.email, ['field_7789'])
      const users = userResult.results || userResult
      
      if (!users || users.length === 0) {
        throw new Error('Identifiants invalides - Email non trouvé')
      }

      const user = users[0]

      // Check if user is active (field_7791 is Status, should be "Actif")
      if (user['field_7791']?.value !== 'Actif') {
        throw new Error('Compte non actif')
      }

      // Validate password using default password system
      if (!credentials.password) {
        throw new Error('Mot de passe requis')
      }

      const expectedPassword = this.generateDefaultPassword(user)
      if (credentials.password !== expectedPassword) {
        throw new Error('Mot de passe incorrect')
      }
      
      // Generate a simple token (in production, use proper JWT)
      const token = `breizhteam_${user.id}_${Date.now()}`

      return {
        token,
        user: {
          id: user.id,
          email: user['field_7789'], // E-mail
          name: `${user['field_7792'] || ''} ${user['field_7790'] || ''}`.trim() || user['field_7789'], // Prénom + Nom
          role: user['field_7800']?.value || 'Membre', // Type
          active: user['field_7791']?.value === 'Actif' // Status
        }
      }
    } catch (error) {
      throw error
    }
  }

  async register(data: RegisterData): Promise<{ token: string; user: User }> {
    try {
      // Check if user already exists
      const existingUser = await this.searchRecords('796', data.email, ['E-mail'])
      if (existingUser.results && existingUser.results.length > 0) {
        throw new Error('Un utilisateur avec cet email existe déjà')
      }

      // Split name into first and last name
      const nameParts = data.name.trim().split(' ')
      const firstName = nameParts[0] || ''
      const lastName = nameParts.slice(1).join(' ') || ''

      // Create new user in table 796 using field IDs
      const newUser = await this.createRecord('796', {
        'field_7789': data.email, // E-mail
        'field_7792': firstName, // Prénom  
        'field_7790': lastName, // Nom
        'field_7791': 3366, // Status: Visiteur (ID from options)
        'field_7800': 3368, // Type: Membre (ID from options)
        // Add other default fields as needed
      })

      const token = `breizhteam_${newUser.id}_${Date.now()}`

      return {
        token,
        user: {
          id: newUser.id,
          email: newUser['field_7789'], // E-mail
          name: `${firstName} ${lastName}`.trim(),
          role: newUser['field_7800']?.value || 'Membre', // Type
          active: newUser['field_7791']?.value === 'Actif' // Status
        }
      }
    } catch (error) {
      throw error
    }
  }

  async verifyToken(token: string): Promise<{ valid: boolean; user?: User }> {
    try {
      // Extract user ID from token (simple implementation)
      const parts = token.split('_')
      if (parts.length !== 3 || parts[0] !== 'breizhteam') {
        return { valid: false }
      }

      const userId = parts[1]
      const user = await this.getRecord('796', userId)

      if (user && user['field_7791']?.value === 'Actif') {
        return {
          valid: true,
          user: {
            id: user.id,
            email: user['field_7789'], // E-mail
            name: `${user['field_7792'] || ''} ${user['field_7790'] || ''}`.trim() || user['field_7789'], // Prénom + Nom
            role: user['field_7800']?.value || 'Membre', // Type
            active: user['field_7791']?.value === 'Actif' // Status
          }
        }
      }

      return { valid: false }
    } catch (error) {
      return { valid: false }
    }
  }

  async updateUser(userId: string, userData: Partial<User>): Promise<User> {
    try {
      // Map user data fields to Baserow field IDs
      const baserowData: any = {}
      
      if (userData.email) baserowData['field_7789'] = userData.email // E-mail
      if (userData.name) {
        const nameParts = userData.name.trim().split(' ')
        baserowData['field_7792'] = nameParts[0] || '' // Prénom
        baserowData['field_7790'] = nameParts.slice(1).join(' ') || '' // Nom
      }
      
      const updatedUser = await this.updateRecord('796', userId, baserowData)
      
      return {
        id: updatedUser.id,
        email: updatedUser['field_7789'], // E-mail
        name: `${updatedUser['field_7792'] || ''} ${updatedUser['field_7790'] || ''}`.trim() || updatedUser['field_7789'], // Prénom + Nom
        role: updatedUser['field_7800']?.value || 'Membre', // Type
        active: updatedUser['field_7791']?.value === 'Actif' // Status
      }
    } catch (error) {
      throw error
    }
  }

  // Charte Graphique methods
  async getCharteGraphique(tableId = '805'): Promise<any> {
    try {
      const result = await this.getRecords(tableId)
      return result.results || result
    } catch (error) {
      console.error('❌ Error fetching Charte Graphique:', error)
      throw error
    }
  }

  async getColorsFromCharte(): Promise<{ [key: string]: string }> {
    try {
      const charteData = await this.getCharteGraphique()
      const colors: { [key: string]: string } = {}
      
      // Process charte data to extract colors using real field structure
      charteData.forEach((item: any) => {
        if (item.field_7894 && item.field_7893 && item.field_7895?.value === 'Couleur') { // Active and has color value and is color type
          const name = item.field_7892 // Element name
          const colorValue = item.field_7893 // Color hex value
          
          if (name && colorValue) {
            // Map French names to English keys
            if (name.toLowerCase().includes('principal')) {
              colors.primary = colorValue
            } else if (name.toLowerCase().includes('secondaire')) {
              colors.secondary = colorValue
            } else {
              // Use the name as key, sanitized
              const key = name.toLowerCase().replace(/[^a-z0-9]/g, '_')
              colors[key] = colorValue
            }
          }
        }
      })
      
      // Ensure we have primary and secondary colors
      if (!colors.primary) colors.primary = '#f39200' // Breizhteam orange
      if (!colors.secondary) colors.secondary = '#000000' // Black
      
      return {
        ...colors,
        // Add computed variations
        accent: colors.primary,
        neutral: '#6b7280',
        success: '#22c55e',
        warning: '#f59e0b',
        error: '#ef4444',
      }
    } catch (error) {
      console.error('❌ Error extracting colors from charte:', error)
      // Return Breizhteam default colors as fallback
      return {
        primary: '#f39200', // Breizhteam orange
        secondary: '#000000', // Black
        accent: '#f39200',
        neutral: '#6b7280',
        success: '#22c55e',
        warning: '#f59e0b',
        error: '#ef4444',
      }
    }
  }

  async getLogoFromCharte(): Promise<string | null> {
    try {
      const charteData = await this.getCharteGraphique()
      
      // Find logo entry
      const logoItem = charteData.find((item: any) => 
        item.field_7894 && // Active
        item.field_7892?.toLowerCase().includes('logo') && // Name contains 'logo'
        item.field_7896 && Array.isArray(item.field_7896) && item.field_7896.length > 0 // Has files
      )
      
      if (logoItem && logoItem.field_7896[0]) {
        return logoItem.field_7896[0].url // Return first file URL
      }
      
      return null
    } catch (error) {
      console.error('❌ Error extracting logo from charte:', error)
      return null
    }
  }

  async getFontsFromCharte(): Promise<string[]> {
    try {
      const charteData = await this.getCharteGraphique()
      const fonts: string[] = []
      
      // Look for font entries (if any)
      charteData.forEach((item: any) => {
        if (item.field_7894 && item.field_7895?.value === 'Police' && item.field_7893) { // Active and font type
          fonts.push(item.field_7893)
        }
      })
      
      return fonts.length > 0 ? fonts : ['Inter', 'Roboto', 'sans-serif']
    } catch (error) {
      console.error('❌ Error extracting fonts from charte:', error)
      return ['Inter', 'Roboto', 'sans-serif']
    }
  }

  // Combined method to get all charte elements at once
  async getCompleteCharte(): Promise<{
    colors: { [key: string]: string }
    logo: string | null
    fonts: string[]
  }> {
    try {
      const [colors, logo, fonts] = await Promise.all([
        this.getColorsFromCharte(),
        this.getLogoFromCharte(),
        this.getFontsFromCharte()
      ])
      
      return { colors, logo, fonts }
    } catch (error) {
      console.error('❌ Error getting complete charte:', error)
      throw error
    }
  }
}

export const baserowService = new BaserowService()